import { ref } from 'vue';

const FIXED_COLUMNS = [
  { key: 'title', labelKey: 'KANBAN.LIST_VIEW.COLUMN_NAME' },
  { key: 'contact', labelKey: 'KANBAN.LIST_VIEW.COLUMN_CONTACT' },
  { key: 'company', labelKey: 'KANBAN.LIST_VIEW.COLUMN_COMPANY' },
  { key: 'phone_number', labelKey: 'KANBAN.LIST_VIEW.COLUMN_PHONE' },
  { key: 'email', labelKey: 'KANBAN.LIST_VIEW.COLUMN_EMAIL' },
  { key: 'city', labelKey: 'KANBAN.LIST_VIEW.COLUMN_CITY' },
  { key: 'opp_source', labelKey: 'KANBAN.LIST_VIEW.COLUMN_SOURCE' },
  { key: 'stage', labelKey: 'KANBAN.LIST_VIEW.COLUMN_STAGE' },
  { key: 'closing_date', labelKey: 'KANBAN.LIST_VIEW.COLUMN_CLOSING_DATE' },
  { key: 'value', labelKey: 'KANBAN.LIST_VIEW.COLUMN_VALUE' },
  { key: 'assignee', labelKey: 'KANBAN.LIST_VIEW.COLUMN_ASSIGNEE' },
  { key: 'opp_products', labelKey: 'KANBAN.LIST_VIEW.COLUMN_PRODUCT' },
  { key: 'watchers', labelKey: 'KANBAN.LIST_VIEW.COLUMN_WATCHERS' },
  { key: 'tags', labelKey: 'KANBAN.LIST_VIEW.COLUMN_TAGS' },
  { key: 'task_count', labelKey: 'KANBAN.LIST_VIEW.COLUMN_TASKS' },
  { key: 'next_meeting', labelKey: 'KANBAN.LIST_VIEW.COLUMN_MEETING' },
];

function storageKey(pipelineId, userId) {
  return `kanban_list_columns_${pipelineId}_${userId}`;
}

function buildDefaultColumns(customAttributes) {
  const fixed = FIXED_COLUMNS.map(c => ({
    ...c,
    visible: true,
    isCustom: false,
  }));
  const custom = customAttributes.map(attr => ({
    key: attr.attribute_key,
    labelKey: null,
    label: attr.attribute_display_name,
    displayType: attr.attribute_display_type,
    visible: false,
    isCustom: true,
  }));
  return [...fixed, ...custom];
}

function loadPersisted(pipelineId, userId) {
  try {
    const raw = localStorage.getItem(storageKey(pipelineId, userId));
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

function mergeWithPersisted(defaults, persisted) {
  if (!persisted?.length) return defaults;

  const byKey = new Map(defaults.map(c => [c.key, c]));
  const ordered = [];

  persisted.forEach(p => {
    const def = byKey.get(p.key);
    // Entradas persistidas cujo key não existe mais nos defaults (ex: um custom
    // attribute deletado depois de persistido) são descartadas silenciosamente aqui.
    // Isso é intencional: a coluna não deve mais aparecer, e a próxima chamada de
    // persist() já remove essa entrada órfã do localStorage automaticamente.
    if (def) {
      ordered.push({ ...def, visible: p.visible });
      byKey.delete(p.key);
    }
  });

  byKey.forEach(remaining => ordered.push(remaining));

  return ordered;
}

/**
 * Colunas configuráveis para a visualização em Lista do Kanban.
 * Combina as colunas fixas (título, contato, etapa etc.) com as colunas de
 * custom attributes do pipeline, mescla com as preferências de visibilidade/
 * ordem persistidas no localStorage do usuário e expõe ações para mostrar,
 * esconder e reordenar colunas.
 *
 * @param {Object} params
 * @param {string|number} params.pipelineId - id do pipeline do Kanban
 * @param {string|number} params.userId - id do usuário logado, usado para
 *   isolar a persistência por usuário
 * @param {Array} params.customAttributes - custom attributes do pipeline,
 *   usados para gerar colunas adicionais
 * @returns {{ columns: import('vue').Ref<Array>, hideColumn: Function, showColumn: Function, reorderColumn: Function }}
 */
export function useKanbanListColumns({ pipelineId, userId, customAttributes }) {
  const defaults = buildDefaultColumns(customAttributes ?? []);
  const persisted = loadPersisted(pipelineId, userId);
  const columns = ref(mergeWithPersisted(defaults, persisted));

  function persist() {
    const toStore = columns.value.map(c => ({
      key: c.key,
      visible: c.visible,
    }));
    localStorage.setItem(
      storageKey(pipelineId, userId),
      JSON.stringify(toStore)
    );
  }

  function hideColumn(key) {
    const col = columns.value.find(c => c.key === key);
    if (col) col.visible = false;
    persist();
  }

  function showColumn(key) {
    const col = columns.value.find(c => c.key === key);
    if (col) col.visible = true;
    persist();
  }

  function reorderColumn(key, newIndex) {
    const currentIndex = columns.value.findIndex(c => c.key === key);
    if (currentIndex === -1 || currentIndex === newIndex) return;

    const updated = [...columns.value];
    const [moved] = updated.splice(currentIndex, 1);
    updated.splice(newIndex, 0, moved);
    columns.value = updated;
    persist();
  }

  return { columns, hideColumn, showColumn, reorderColumn };
}
